import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core"
import React, { useCallback } from "react"
import {
  ISaleInvoiceSummary,
  ISaleReportResponse,
  ISaleReportTotals,
} from "types/response/saleReportResponse"
import { CustomFilters } from "./CustomFilters"
import {
  exportSaleReportService,
  getSaleReportService,
} from "lib/utils/api/ledgerSummary"
import ExportReportDialog from "./ExportReportDialog"
import {
  COLOR_BORDER_LIGHT,
  COLOR_CHIP_BG,
  COLOR_ICON_EMPTY,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_SURFACE_LIGHT,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_HINT,
  COLOR_TEXT_PLACEHOLDER,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_BORDER,
  COLOR_TEXT_SUBTLE,
  COLOR_WHITE,
  DEFAULT_FILTER_CONFIG,
  FILTER_FIELDS,
  FILTER_FIELD_LABELS,
  FilterConfig,
  FilterField,
  HEADER_FIXED_COLUMNS,
  MESES_LIST,
  PAGE_SIZE,
  QUINCENA_VALUE,
  Quincena,
  SALE_REPORT_COLUMNS,
} from "./constants"
import { FiltrosSup } from "./CustomFilters"
import { totalCellStyle, useDashboardStyles } from "./styles"

const SaleReportGrid = () => {
  const classes = useDashboardStyles()

  const mesActualIndex: number = new Date().getMonth()

  // --- ESTADOS DE UI ---
  const [anchorElCol, setAnchorElCol] = React.useState<HTMLElement | null>(null)
  const [anchorElFilter, setAnchorElFilter] = React.useState<HTMLElement | null>(null)
  const [openExportDialog, setOpenExportDialog] = React.useState<boolean>(false)
  const [dragIndex, setDragIndex] = React.useState<number | null>(null)

  // --- ESTADOS DE COLUMNAS ---
  const [orderedColumns, setOrderedColumns] = React.useState<typeof SALE_REPORT_COLUMNS>(SALE_REPORT_COLUMNS)
  const [visibleColumns, setVisibleColumns] = React.useState<Set<string>>(
    new Set(SALE_REPORT_COLUMNS.map((c) => c.id)),
  )

  // --- ESTADOS DE DATA ---
  const [ventas, setVentas] = React.useState<ISaleInvoiceSummary[]>([])
  const [totals, setTotals] = React.useState<ISaleReportTotals | null>(null)
  const [page, setPage] = React.useState<number>(0)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [hasMore, setHasMore] = React.useState<boolean>(true)

  // --- ESTADOS DE FILTROS ---
  const [filtrosSup, setFiltrosSup] = React.useState<FiltrosSup>({
    anio: String(new Date().getFullYear()),
    mes: MESES_LIST[mesActualIndex],
    mesNumero: mesActualIndex + 1,
    quincena: Quincena.Q1,
  })

  const [filterConfig, setFilterConfig] = React.useState<FilterConfig>(DEFAULT_FILTER_CONFIG)

  const handleExportReport = async (emails: string[]) => {
    await exportSaleReportService(
      {
        year: filtrosSup.anio,
        month: filtrosSup.mesNumero,
        quincena: QUINCENA_VALUE[filtrosSup.quincena as Quincena],
        invoiceNumber: filterConfig.noDoc.join(",") || undefined,
        rif: filterConfig.rif.join(",") || undefined,
        clientName: filterConfig.clientName.join(",") || undefined,
      },
      emails,
    )
  }

  // --- CARGA DE DATA ---
  const loadVentas = useCallback(
    async (currentPage: number, isNewSearch = false, overrideFilters?: any) => {
      if (loading || (!hasMore && !isNewSearch)) return
      setLoading(true)
      try {
        const targetFilters = overrideFilters || filterConfig
        const params = {
          page: currentPage,
          size: PAGE_SIZE,
          year: filtrosSup.anio,
          month: filtrosSup.mesNumero,
          quincena: QUINCENA_VALUE[filtrosSup.quincena as Quincena],
          invoiceNumber: targetFilters.noDoc.join(","),
          rif: targetFilters.rif.join(","),
          clientName: targetFilters.clientName.join(","),
        }

        const data: ISaleReportResponse = await getSaleReportService(params)
        const nuevas = data.invoices.content

        setVentas((prev) => (isNewSearch ? nuevas : [...prev, ...nuevas]))
        if (isNewSearch) setTotals(data.totals || null)
        setHasMore(!data.invoices.last)
        setPage(currentPage + 1)
      } catch (error) {
        console.error("Error cargando ventas:", error)
      } finally {
        setLoading(false)
      }
    },
    [filtrosSup, filterConfig, loading, hasMore],
  )

  React.useEffect(() => {
    loadVentas(0, true)
  }, [filtrosSup.anio, filtrosSup.mesNumero, filtrosSup.quincena])

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop <= clientHeight + 100 && !loading && hasMore) {
      loadVentas(page)
    }
  }

  const visibleOrdered = orderedColumns.filter((col) =>
    visibleColumns.has(col.id),
  )

  return (
    <Box>
      <Box
        style={{
          width: "100%",
          padding: "5px 20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          style={{
            color: COLOR_PRIMARY,
            fontWeight: 900,
            textTransform: "uppercase",
          }}
        >
          LIBRO DE VENTAS
        </Typography>
      </Box>

      <CustomFilters filtrosSup={filtrosSup} setFiltrosSup={setFiltrosSup} />

      <Box className={classes.toolbar}>
        <Button
          size="small"
          variant="contained"
          startIcon={<Icon style={{ fontSize: 18, color: COLOR_WHITE }}>download</Icon>}
          onClick={() => setOpenExportDialog(true)}
          style={{
            backgroundColor: COLOR_PRIMARY,
            color: COLOR_WHITE,
            fontWeight: 600,
            borderRadius: 20,
            textTransform: "none",
            fontSize: "0.72rem",
            padding: "4px 14px",
            boxShadow: "0px 3px 10px rgba(212,5,17,0.4)",
          }}
        >
          Descargar Reporte
        </Button>
        <IconButton
          onClick={(e) => setAnchorElFilter(e.currentTarget)}
          style={{ padding: 8 }}
        >
          <Icon style={{ fontSize: 21 }}>filter_list</Icon>
        </IconButton>
        <IconButton
          onClick={(e) => setAnchorElCol(e.currentTarget)}
          style={{ padding: 8 }}
        >
          <Icon style={{ fontSize: 20 }}>view_column</Icon>
        </IconButton>
      </Box>

      {/* POPOVER: COLUMNAS */}
      <Popover
        open={Boolean(anchorElCol)}
        anchorEl={anchorElCol}
        onClose={() => setAnchorElCol(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            borderRadius: "16px",
            boxShadow: "0px 12px 40px rgba(0,0,0,0.18)",
            width: "320px",
            border: `1px solid ${COLOR_BORDER_LIGHT}`,
          },
        }}
      >
        <Box className={classes.popoverHeader}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.9)" }}>
              view_column
            </Icon>
            <Typography variant="subtitle1" className={classes.popoverHeaderTitle}>
              Columnas visibles
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setAnchorElCol(null)}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }}>
              close
            </Icon>
          </IconButton>
        </Box>

        <Box className={classes.popoverActions}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon style={{ fontSize: 15 }}>done_all</Icon>}
            style={{
              fontSize: "0.75rem",
              padding: "3px 10px",
              borderColor: COLOR_PRIMARY,
              color: COLOR_PRIMARY,
              borderRadius: 8,
              textTransform: "none",
              flex: 1,
            }}
            onClick={() =>
              setVisibleColumns(new Set(orderedColumns.map((c) => c.id)))
            }
          >
            Todas
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon style={{ fontSize: 15 }}>remove_done</Icon>}
            style={{
              fontSize: "0.75rem",
              padding: "3px 10px",
              borderColor: COLOR_TEXT_PLACEHOLDER,
              color: COLOR_TEXT_SUBTLE,
              borderRadius: 8,
              textTransform: "none",
              flex: 1,
            }}
            onClick={() => setVisibleColumns(new Set())}
          >
            Ninguna
          </Button>
        </Box>

        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 18px 4px",
            backgroundColor: COLOR_SURFACE_LIGHT,
          }}
        >
          <Icon style={{ fontSize: 13, color: COLOR_TEXT_DISABLED }}>info_outline</Icon>
          <Typography variant="caption" style={{ color: COLOR_TEXT_DISABLED, fontSize: "0.7rem" }}>
            Arrastra para reordenar las columnas
          </Typography>
        </Box>

        <Divider style={{ backgroundColor: COLOR_BORDER_LIGHT }} />

        <Box style={{ maxHeight: 340, overflowY: "auto", padding: "6px 0 10px" }}>
          {orderedColumns.map((col, index) => (
            <Box
              key={col.id}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => {
                e.preventDefault()
                if (dragIndex === null || dragIndex === index) return
                setOrderedColumns((prev) => {
                  const next = [...prev]
                  const [moved] = next.splice(dragIndex, 1)
                  next.splice(index, 0, moved)
                  return next
                })
                setDragIndex(index)
              }}
              onDragEnd={() => setDragIndex(null)}
              className={classes.draggableRow}
              style={{
                backgroundColor: dragIndex === index ? COLOR_CHIP_BG : "transparent",
                borderLeft:
                  dragIndex === index
                    ? `3px solid ${COLOR_PRIMARY}`
                    : "3px solid transparent",
              }}
            >
              <Icon
                style={{
                  fontSize: 22,
                  color: dragIndex === index ? COLOR_PRIMARY : COLOR_TEXT_BORDER,
                  flexShrink: 0,
                }}
              >
                drag_indicator
              </Icon>
              <input
                type="checkbox"
                checked={visibleColumns.has(col.id)}
                onChange={(e) => {
                  setVisibleColumns((prev) => {
                    const next = new Set(prev)
                    if (e.target.checked) next.add(col.id)
                    else next.delete(col.id)
                    return next
                  })
                }}
                style={
                  {
                    accentColor: COLOR_PRIMARY,
                    width: 16,
                    height: 16,
                    cursor: "pointer",
                    flexShrink: 0,
                  } as React.CSSProperties
                }
              />
              <Typography
                variant="body2"
                style={{
                  fontSize: "0.88rem",
                  fontWeight: visibleColumns.has(col.id) ? 600 : 400,
                  color: visibleColumns.has(col.id) ? COLOR_TEXT_PRIMARY : COLOR_TEXT_PLACEHOLDER,
                  flex: 1,
                }}
              >
                {col.label}
              </Typography>
              {visibleColumns.has(col.id) && (
                <Icon style={{ fontSize: 16, color: COLOR_PRIMARY }}>visibility</Icon>
              )}
            </Box>
          ))}
        </Box>

        <Box className={classes.popoverFooter}>
          <Typography variant="caption" style={{ color: COLOR_TEXT_HINT, fontSize: "0.75rem" }}>
            {visibleColumns.size} de {orderedColumns.length} columnas visibles
          </Typography>
          <Icon style={{ fontSize: 15, color: COLOR_TEXT_BORDER }}>drag_handle</Icon>
        </Box>
      </Popover>

      {/* POPOVER: FILTROS */}
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={() => setAnchorElFilter(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            borderRadius: "16px",
            width: "550px",
            boxShadow: "0px 12px 40px rgba(0,0,0,0.18)",
            border: `1px solid ${COLOR_BORDER_LIGHT}`,
          },
        }}
      >
        <Box className={classes.popoverHeader}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.9)" }}>
              filter_list
            </Icon>
            <Typography variant="subtitle1" className={classes.popoverHeaderTitle}>
              Filtros Combinados
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setAnchorElFilter(null)}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }}>
              close
            </Icon>
          </IconButton>
        </Box>

        <Box style={{ padding: "16px 20px 20px" }}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              gap: "32px",
              marginBottom: 14,
            }}
          >
            <FormControl variant="standard" style={{ width: "180px", flexShrink: 0 }}>
              <InputLabel style={{ fontSize: "0.85rem" }}>Campo</InputLabel>
              <Select
                value={filterConfig.activeField}
                onChange={(e) =>
                  setFilterConfig({
                    ...filterConfig,
                    activeField: e.target.value as FilterField,
                  })
                }
              >
                {FILTER_FIELDS.map(({ field, label }) => (
                  <MenuItem key={field} value={field}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              variant="standard"
              label={`Agregar ${FILTER_FIELD_LABELS[filterConfig.activeField]} y presione Enter`}
              fullWidth
              value={filterConfig.inputValue}
              onChange={(e) =>
                setFilterConfig({ ...filterConfig, inputValue: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && filterConfig.inputValue.trim()) {
                  const field = filterConfig.activeField
                  if (!filterConfig[field].includes(filterConfig.inputValue.trim())) {
                    setFilterConfig({
                      ...filterConfig,
                      [field]: [...filterConfig[field], filterConfig.inputValue.trim()],
                      inputValue: "",
                    })
                  } else {
                    setFilterConfig({ ...filterConfig, inputValue: "" })
                  }
                }
              }}
            />
          </Box>

          <Divider style={{ backgroundColor: COLOR_BORDER_LIGHT, marginBottom: 12 }} />

          <Box className={classes.filterChipsContainer}>
            {FILTER_FIELDS.map(({ field, label }) => {
              const chips = filterConfig[field as FilterField] as string[]
              if (chips.length === 0) return null
              return (
                <Box key={field} style={{ marginBottom: 6 }}>
                  <Box className={classes.filterCategoryChip}>
                    <Typography
                      variant="caption"
                      style={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        color: COLOR_WHITE,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <Box style={{ display: "flex", flexWrap: "wrap" }}>
                    {chips.map((val, i) => (
                      <Chip
                        key={i}
                        label={val}
                        size="small"
                        style={{
                          margin: 3,
                          backgroundColor: COLOR_CHIP_BG,
                          color: COLOR_PRIMARY_DARK,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          borderRadius: 6,
                        }}
                        onDelete={() => {
                          const updated = chips.filter((_, idx) => idx !== i)
                          setFilterConfig({ ...filterConfig, [field]: updated })
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )
            })}

            {filterConfig.noDoc.length === 0 &&
              filterConfig.rif.length === 0 &&
              filterConfig.clientName.length === 0 && (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 80,
                    gap: 6,
                  }}
                >
                  <Icon style={{ fontSize: 28, color: COLOR_ICON_EMPTY }}>filter_list_off</Icon>
                  <Typography variant="caption" style={{ color: COLOR_TEXT_PLACEHOLDER }}>
                    No hay filtros activos
                  </Typography>
                </Box>
              )}
          </Box>
        </Box>

        <Box
          style={{
            borderTop: `1px solid ${COLOR_BORDER_LIGHT}`,
            padding: "10px 20px 14px",
            backgroundColor: COLOR_SURFACE_LIGHT,
            borderRadius: "0 0 16px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon style={{ fontSize: 16 }}>delete_sweep</Icon>}
            style={{
              color: COLOR_PRIMARY_DARK,
              borderColor: COLOR_PRIMARY_DARK,
              borderRadius: 8,
              textTransform: "none",
              fontSize: "0.8rem",
            }}
            onClick={() => {
              setFilterConfig(DEFAULT_FILTER_CONFIG)
              loadVentas(0, true, DEFAULT_FILTER_CONFIG)
              setAnchorElFilter(null)
            }}
          >
            Limpiar Todo
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Icon style={{ fontSize: 16 }}>check</Icon>}
            style={{
              backgroundColor: COLOR_PRIMARY,
              color: COLOR_WHITE,
              borderRadius: 8,
              textTransform: "none",
              fontSize: "0.8rem",
              boxShadow: "0px 4px 12px rgba(212,5,17,0.35)",
            }}
            onClick={() => {
              loadVentas(0, true)
              setAnchorElFilter(null)
            }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      </Popover>

      {/* TABLA */}
      <TableContainer
        component={Paper}
        onScroll={handleScroll}
        style={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}
      >
        <Table
          stickyHeader
          size="small"
          style={{
            tableLayout: "fixed",
            width: "fit-content",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHead>
            <TableRow>
              {HEADER_FIXED_COLUMNS.map((col) => (
                <TableCell
                  key={col.label}
                  align="center"
                  className={classes.headerCell}
                  style={{ width: col.width }}
                >
                  {col.label}
                </TableCell>
              ))}
              {visibleOrdered.map((col) => (
                <TableCell
                  key={col.id}
                  align="center"
                  className={classes.headerCell}
                  style={{ width: 140 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ventas.map((row, index) => (
              <TableRow key={`${row.invoiceNumber}-${index}`} hover>
                <TableCell
                  align="center"
                  className={classes.fixedCell}
                  style={{ width: 120 }}
                >
                  {row.invoiceNumber}
                </TableCell>
                <TableCell
                  align="left"
                  className={classes.dataCell}
                  style={{ width: 120 }}
                >
                  {row.rif}
                </TableCell>
                <TableCell
                  align="left"
                  className={classes.dataCell}
                  style={{ width: 300, whiteSpace: "normal" }}
                >
                  {row.clientName}
                </TableCell>
                {visibleOrdered.map((col) => {
                  const value = row[col.id] as number | null
                  return (
                    <TableCell
                      key={col.id}
                      align="right"
                      className={classes.dataCell}
                      style={{ width: 140 }}
                    >
                      {value != null
                        ? value.toLocaleString("es-VE", { minimumFractionDigits: 2 })
                        : ""}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell align="center" style={{ ...totalCellStyle, width: 120 }}>
                Total
              </TableCell>
              <TableCell style={{ ...totalCellStyle, width: 120 }} />
              <TableCell style={{ ...totalCellStyle, width: 300 }} />
              {visibleOrdered.map((col) => (
                <TableCell
                  key={col.id}
                  align="right"
                  style={{ ...totalCellStyle, width: 140 }}
                >
                  {totals &&
                  ventas.length > 0 &&
                  totals[col.id as keyof ISaleReportTotals] != null
                    ? (
                        totals[col.id as keyof ISaleReportTotals] as number
                      ).toLocaleString("es-VE", { minimumFractionDigits: 2 })
                    : ""}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
        {loading && (
          <Box style={{ display: "flex", justifyContent: "center", padding: 16 }}>
            <CircularProgress size={30} />
          </Box>
        )}
        {!loading && ventas.length === 0 && (
          <Box className={classes.emptyState}>
            <Icon style={{ fontSize: 40, color: COLOR_ICON_EMPTY }}>search_off</Icon>
            <Typography variant="body2" style={{ color: COLOR_TEXT_DISABLED, fontWeight: 500 }}>
              No se encontraron registros para los filtros aplicados
            </Typography>
          </Box>
        )}
      </TableContainer>

      <ExportReportDialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        onConfirm={handleExportReport}
        title="Descargar Reporte de Ventas"
        description="El reporte se generará con los filtros activos y será enviado a los correos indicados."
      />
    </Box>
  )
}

export default SaleReportGrid
